import _ from 'lodash';
import React from 'react';
import { useObserver } from 'mobx-react-lite';

import { useGetRecommendedProductsQuery } from 'types/graphql';
import { PersonalizedProductsQuery } from 'src/personalization/data-layer/types';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import BlueSparkle from 'src/assets/blue-sparkle-icon';
import useStores from 'shared/hooks/use-stores';

import { DesktopOnly, MobileOnly } from 'shared/components';
import { BlendedPersonalizedProductCarousel } from './blended-carousel';
import { TabbedPersonalizedProductCarousel } from './tabbed-carousel';
import { useWidgetOrders } from './use-widget-orders';

type LoggedInProps = {
  personalizedProductsQuery: PersonalizedProductsQuery;
};

export const LoggedInV2 = (props: LoggedInProps): JSX.Element | null => {
  const { User } = useStores();
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id;
  const isLoggedIn = useObserver(() => User?.isLoggedIn);

  const heading = 'Recommended for you';
  const subheading = 'Based on your shopping history';
  const icon = <BlueSparkle />;

  // Skip "Buy it Again" query for public session phone login to protect user privacy
  const isPublicSessionPhoneLogin = User?.isPublicSessionPhoneNumberLogin ?? false;
  const shouldSkipRecommendedProducts = isPublicSessionPhoneLogin || !dispensaryId || !isLoggedIn;

  const recommendedProductsQuery = useGetRecommendedProductsQuery({
    fetchPolicy: `network-only`,
    skip: shouldSkipRecommendedProducts,
    variables: {
      dispensaryId: dispensaryId ?? '',
    },
  });

  const productQueries = {
    ...props,
    recommendedProductsQuery: _.pick(recommendedProductsQuery, ['data', 'loading', 'error']),
  };

  const widgetOrders = useWidgetOrders();

  return (
    <>
      <MobileOnly mediaQuery='xs'>
        <TabbedPersonalizedProductCarousel
          heading={heading}
          subheading={subheading}
          icon={icon}
          {...productQueries}
          widgetOrders={widgetOrders}
        />
      </MobileOnly>

      <DesktopOnly mediaQuery='sm'>
        <BlendedPersonalizedProductCarousel
          heading={heading}
          subheading={subheading}
          icon={icon}
          {...productQueries}
          widgetOrders={widgetOrders}
        />
      </DesktopOnly>
    </>
  );
};
